import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  HStack,
  Icon,
  Table,
  TableContainer,
  Tbody,
  Td,
  Tfoot,
  Th,
  Thead,
  Tr,
  Tooltip,
  Flex,
} from '@chakra-ui/react';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Text } from '@chakra-ui/layout';
import { FaInfoCircle } from 'react-icons/fa';
import { formatCurrency } from '~/utils/formatCurrency';
import { ContractEquipment } from '~/types';
import { InputFloat } from '~/components/InputFloat';
import { Input } from '~/components';

interface ContractEquipamentValuesProps {
  valor_mensal_contrato: Number;
  visualizacao: boolean;
  equipamentos: ContractEquipment[];
  changeListaEquipamentos: (equipamentos: ContractEquipment[]) => void;
}
const contractEquipamentValue = yup.object().shape({
  valor: yup
    .number()
    .required('Valor obrigatório')
    .min(0, 'Valor deve ser maior que 0'),
});

export const ContractEquipamentValues: React.FC<ContractEquipamentValuesProps> =
  ({
    valor_mensal_contrato,
    visualizacao,
    equipamentos,
    changeListaEquipamentos,
  }: ContractEquipamentValuesProps) => {
    const { formState, clearErrors, setError } = useForm({
      resolver: yupResolver(contractEquipamentValue),
    });
    const { errors } = formState;
    const [isLoading, setIsLoading] = useState(false);
    const [listEquipaments, setListEquipaments] =
      useState<ContractEquipment[]>(equipamentos);

    const [totalFormatado, setTotalFormatado] = React.useState(
      formatCurrency(
        parseFloat(
          String(
            equipamentos.reduce(
              (acc, curr) => acc + Number(curr.valor_mensal),
              0,
            ),
          ),
        ),
      ).replace('R$', ''),
    );
    const [totalContratoFormatado, setTotalContratoFormatado] = React.useState(
      formatCurrency(parseFloat(String(valor_mensal_contrato))).replace(
        'R$',
        '',
      ),
    );
    const [total, setTotal] = React.useState(
      equipamentos.reduce((acc, curr) => acc + Number(curr.valor_mensal), 0),
    );

    function handleEquipament(
      eq: ContractEquipment,
      key: keyof ContractEquipment,
    ) {
      const value = eq[key];
      clearErrors(`${key}-${eq.cd_equipamento}`);

      const lista = listEquipaments.map(equipamento => {
        if (equipamento.cd_equipamento === eq.cd_equipamento) {
          return {
            ...eq,
          };
        }
        return equipamento;
      });

      setListEquipaments(lista);
      changeListaEquipamentos(lista);
      value === 0 &&
        setError(`${key}-${eq.cd_equipamento}`, {
          message: 'Valor obrigatório',
        });

      key === 'valor_mensal_unitario' &&
        value > 999999999.99 &&
        setError(`${key}-${eq.cd_equipamento}`, {
          message: 'Valor deve ser menor que 999.999.999,99',
        });

      const tot = lista.reduce(
        (acc, curr) => acc + Number(curr.valor_mensal),
        0,
      );
      setTotal(tot);
      setTotalFormatado(formatCurrency(tot).replace('R$', ''));
    }
    function handleValor(e: number, eq: ContractEquipment) {
      clearErrors(`valor-${eq.cd_equipamento}`);
      const lista = listEquipaments.map(equipamento => {
        if (equipamento.cd_equipamento === eq.cd_equipamento) {
          return { ...equipamento, valor_mensal: e };
        }
        return equipamento;
      });
      setListEquipaments(lista);
      changeListaEquipamentos(lista);
      e === 0 &&
        setError(`valor-${eq.cd_equipamento}`, {
          message: 'Valor obrigatório',
        });
      const tot = lista.reduce(
        (acc, curr) => acc + Number(curr.valor_mensal),
        0,
      );
      setTotal(tot);
      setTotalFormatado(formatCurrency(tot).replace('R$', ''));
    }
    return (
      <Accordion defaultIndex={[0]} allowMultiple backgroundColor="#F2F2F2">
        <AccordionItem>
          <h2>
            <AccordionButton fontSize="sm" justifyContent="space-between">
              <HStack>
                <AccordionIcon />
                <Box as="span" flex="1" textAlign="left" ml={2}>
                  Valores dos equipamentos do Contrato
                </Box>
              </HStack>
              <Flex
                textAlign="right"
                whiteSpace="nowrap"
                gridGap={1}
                justifyContent="space-between"
              >
                {equipamentos.length &&
                  parseFloat(valor_mensal_contrato.toFixed(2)) !==
                    parseFloat(total.toFixed(2)) && (
                    <Tooltip
                      hasArrow
                      label="Os valores diferem"
                      bg="gray"
                      shouldWrapChildren
                    >
                      <Icon as={FaInfoCircle} color="red.400" />
                    </Tooltip>
                  )}
                <Text as="span">Valor atual do Contrato:</Text>
                <Text
                  as="span"
                  fontWeight="bold"
                  marginLeft="0.2rem !important"
                >
                  R${totalContratoFormatado}
                </Text>
                <Tooltip
                  hasArrow
                  label="O valor atual (mensal) de cada equipamento é dado por: Valor atual do contrato (Portal) * (valor mensal do equipamento (SAP) / soma do valor mensal de todos os equipamentos)"
                  bg="gray"
                  shouldWrapChildren
                >
                  <Icon as={FaInfoCircle} />
                </Tooltip>
              </Flex>
            </AccordionButton>
          </h2>
          <AccordionPanel p={0} backgroundColor="#FFFFFF" m={2}>
            <TableContainer>
              <Table size="sm">
                <Thead>
                  <Tr>
                    <Th border="2px solid #F2F2F2">CÓDIGO</Th>
                    <Th border="2px solid #F2F2F2">TIPO</Th>
                    <Th border="2px solid #F2F2F2">MODELO</Th>
                    <Th
                      border="2px solid #F2F2F2"
                      width="10%"
                      style={{ textAlign: 'center' }}
                    >
                      QUANTIDADE ORIGINAL
                    </Th>
                    <Th
                      border="2px solid #F2F2F2"
                      width="10%"
                      style={{ textAlign: 'center' }}
                    >
                      QUANTIDADE
                    </Th>
                    <Th
                      border="2px solid #F2F2F2"
                      style={{ textAlign: 'center' }}
                    >
                      VALOR UNITÁRIO
                    </Th>
                    <Th border="2px solid #F2F2F2" width="20%">
                      VALOR ATUAL (MENSAL)
                    </Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {listEquipaments?.length &&
                    listEquipaments.map(eq => (
                      <Tr key={eq.cd_equipamento}>
                        <Td border="2px solid #F2F2F2">{eq.cd_equipamento}</Td>
                        <Td border="2px solid #F2F2F2">
                          {eq.tipo_equipamento}
                        </Td>
                        <Td border="2px solid #F2F2F2">{eq.ds_equipamento}</Td>
                        <Td
                          border="2px solid #F2F2F2"
                          style={{ textAlign: 'center' }}
                        >
                          {eq.quantidade_original}
                        </Td>
                        <Td
                          border="2px solid #F2F2F2"
                          style={{ textAlign: 'center' }}
                        >
                          <HStack alignItems="center" gridGap={2}>
                            <Input
                              name={`quantidade-${eq.cd_equipamento}`}
                              error={errors[`quantidade-${eq.cd_equipamento}`]!}
                              value={eq.quantidade}
                              onChange={e => {
                                const value = e.currentTarget.value.replace(
                                  /\D/g,
                                  '',
                                );
                                handleEquipament(
                                  {
                                    ...eq,
                                    quantidade: Number(value),
                                    valor_mensal:
                                      Math.round(
                                        Number(value) *
                                          eq.valor_mensal_unitario *
                                          100,
                                      ) / 100,
                                  },
                                  'quantidade',
                                );
                              }}
                              fontSize="15px"
                              size="xs"
                              textAlign="end"
                              isDisabled={visualizacao}
                              pattern="[0-9]*"
                            />
                          </HStack>
                        </Td>
                        <Td border="2px solid #F2F2F2" padding="2px 3px">
                          <HStack alignItems="center" gridGap={2}>
                            <span>R$</span>
                            <InputFloat
                              name={`valor_mensal_unitario-${eq.cd_equipamento}`}
                              error={
                                errors[
                                  `valor_mensal_unitario-${eq.cd_equipamento}`
                                ]!
                              }
                              value={eq.valor_mensal_unitario}
                              onChangeValue={e =>
                                handleEquipament(
                                  {
                                    ...eq,
                                    valor_mensal_unitario: e,
                                    valor_mensal:
                                      Math.round(e * eq.quantidade * 100) / 100,
                                  },
                                  'valor_mensal_unitario',
                                )
                              }
                              fontSize="15px"
                              size="xs"
                              textAlign="end"
                              isDisabled={visualizacao}
                            />
                          </HStack>
                        </Td>
                        <Td border="2px solid #F2F2F2" padding="2px 3px">
                          <HStack alignItems="center" gridGap={2}>
                            <span>R$</span>
                            <InputFloat
                              name={`valor-${eq.cd_equipamento}`}
                              error={errors[`valor-${eq.cd_equipamento}`]!}
                              value={eq.valor_mensal}
                              onChangeValue={e => handleValor(e, eq)}
                              fontSize="15px"
                              size="xs"
                              textAlign="end"
                              isDisabled={visualizacao}
                            />
                          </HStack>
                        </Td>
                      </Tr>
                    ))}
                </Tbody>
                <Tfoot>
                  <Tr>
                    <Th colSpan={6} textAlign="end" border="2px solid #F2F2F2">
                      Total
                    </Th>
                    <Th
                      border="2px solid #F2F2F2"
                      fontWeight="bold"
                      padding="2px 3px"
                      fontSize="14px"
                      color="blackAlpha.700"
                    >
                      <HStack
                        alignItems="center"
                        justifyContent="space-between"
                      >
                        <span>R$</span>
                        <Text>{totalFormatado}</Text>
                      </HStack>
                    </Th>
                  </Tr>
                </Tfoot>
              </Table>
            </TableContainer>
          </AccordionPanel>
        </AccordionItem>
      </Accordion>
    );
  };
